import type { SimplifiedProductDraft } from '../../redux/ownerActivationSlice';
import type { ProductFormData } from '../products/newproducts/productFormTypes';

/**
 * Draft mapping for the wizard's simplified first-product step (US3, T055;
 * product-creation contract §9).
 *
 * Only the simplified text/numeric fields persist in activation state; image
 * base64/URIs, barcodes, variants, and other media are never persisted.
 */

/** Seed the per-instance product form from a persisted simplified draft. */
export const draftToFormSeed = (
  draft: SimplifiedProductDraft | null | undefined,
): Partial<ProductFormData> => {
  if (!draft) return {};
  return {
    productname: draft.productName || '',
    category: draft.category || '',
    buyingprice: draft.buyingPrice || '',
    sellingprice: draft.sellingPrice || '',
    quantity: draft.quantity || '',
  };
};

export type SimplifiedDraftValues = Omit<SimplifiedProductDraft, 'updatedAt'>;

/**
 * Project the live form back to the persistable simplified draft fields.
 * Only the five simplified fields are read; media/barcode/variants are never
 * persisted.
 */
export const formToDraftValues = (
  formData: Pick<
    ProductFormData,
    'productname' | 'category' | 'buyingprice' | 'sellingprice' | 'quantity'
  > &
    Partial<ProductFormData>,
): SimplifiedDraftValues => ({
  productName: formData.productname || '',
  category: formData.category || '',
  buyingPrice: formData.buyingprice || '',
  sellingPrice: formData.sellingprice || '',
  quantity: formData.quantity || '',
});

/** True when any simplified draft field carries user input worth persisting. */
export const hasDraftContent = (values: SimplifiedDraftValues): boolean =>
  Boolean(
    values.productName.trim() ||
      values.category.trim() ||
      values.buyingPrice.trim() ||
      values.sellingPrice.trim() ||
      values.quantity.trim(),
  );

/** True when two simplified draft projections differ (skip redundant writes). */
export const draftValuesDiffer = (
  a: SimplifiedDraftValues,
  b: SimplifiedDraftValues,
): boolean =>
  a.productName !== b.productName ||
  a.category !== b.category ||
  a.buyingPrice !== b.buyingPrice ||
  a.sellingPrice !== b.sellingPrice ||
  a.quantity !== b.quantity;
